/**
 * Insulation Tool - Batting Insulation Creation
 * 
 * Creates insulation lines.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface InsulationGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    width: number;
}

export interface InsulationElement extends Element {
    category: 'Detail Items';
    geometryData: InsulationGeometryData;
}

export class InsulationTool extends BaseTool {
    id = 'insulation';
    name = 'Insulation';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.createInsulation(this.startPoint, snapped);

            // Chain
            this.startPoint = snapped;
            this.currentPoint = snapped;
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'line', // Should look like batting
                start: this.startPoint,
                end: this.currentPoint
                // No fixed color: the viewport draws previews with the
                // theme ink (white in dark mode, black in light mode)
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createInsulation(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        // Batting width comes from the options bar (mm), BIM default 89 mm.
        const widthMm = this.context.bimStore.toolProperties.insulationWidth ?? 89;
        const geometryData: InsulationGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            width: widthMm / 1000,
        };

        const insulation: InsulationElement = {
            id: uuidv4(),
            category: 'Detail Items',
            type: 'Insulation Batting',
            parameters: {
                'Length': start.distanceTo(end).toFixed(2) + ' m',
                'Width': `${widthMm} mm`,
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(insulation);
    }
}
